/**
 * HashCalculator - SHA-256 for script content
 *
 * Calculates hashes for inline scripts and external scripts (only same-origin).
 * Normalizes script_url to a stable path (no query/fragment, no trailing slash,
 * collapsed slashes) so the backend baseline can match. Inline content is
 * normalized (line endings + trim) before hashing for deterministic hashes.
 */
export class HashCalculator {
	async sha256Hex(content) {
		if (typeof content !== 'string') return null;
		if (typeof crypto === 'undefined' || !crypto.subtle) return null;

		const data = new TextEncoder().encode(content);
		const digest = await crypto.subtle.digest('SHA-256', data);
		return this.toHex(digest);
	}

	normalizeInlineContent(text) {
		return this.normalizeScriptContent(text);
	}

	async fetchTextSameOrigin(url) {
		if (typeof window === 'undefined') return null;
		const u = new URL(url, window.location.href);
		if (u.origin !== window.location.origin) return null;

		const res = await fetch(u.href, { method: 'GET', credentials: 'same-origin' });
		if (!res.ok) return null;
		return await res.text();
	}

	/**
	 * Normalize script content before hashing.
	 */
	normalizeScriptContent(text) {
		if (typeof text !== 'string') return '';
		if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
		return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
	}

	/**
	 * Hash external script
	 */
	async sha256HexFromUrlSameOrigin(url) {
		try {
			const text = await this.fetchTextSameOrigin(url);
			if (text == null) return null;
			const normalized = this.normalizeScriptContent(text);
			return await this.sha256Hex(normalized);
		} catch {
			return null;
		}
	}

	/**
	 * Normalize script URL before hashing and before sending. Single source for
	 * normalized_script_url (consistent with backend). Removes querystring and
	 * fragment; normalizes slashes and trailing slash. For first-party only (path).
	 */
	normalizeScriptUrl(url) {
		try {
			if (typeof window === 'undefined') return String(url || '');
			const u = new URL(url, window.location.href);
			return this._normalizedPath(u);
		} catch {
			return String(url || '');
		}
	}

	/** Path only: no query, no fragment, no leading/trailing slash, collapsed slashes. */
	_normalizedPath(u) {
		let path = u.pathname || '';
		if (path.startsWith('/')) path = path.slice(1);
		path = path.replace(/\/+$/, '');
		path = path.replace(/\/+/g, '/');
		return path;
	}

	/**
	 * For third-party scripts: normalized_script_url must include origin to avoid
	 * collisions (e.g. cdn.example.com/path/lib.js). Same path rules as normalizeScriptUrl.
	 */
	normalizeScriptUrlWithOrigin(url) {
		try {
			if (typeof window === 'undefined') return String(url || '');
			const u = new URL(url, window.location.href);
			const path = this._normalizedPath(u);
			return path ? `${u.hostname}/${path}` : u.hostname;
		} catch {
			return String(url || '');
		}
	}

	/**
	 * True if url is same-origin with the page (can be fetched for hash).
	 */
	isSameOrigin(url) {
		if (typeof window === 'undefined') return false;
		try {
			const u = new URL(url, window.location.href);
			return u.origin === window.location.origin;
		} catch {
			return false;
		}
	}

	/**
	 * True if script is cross-origin (third-party). Cannot be hashed; report with
	 * source=third_party, can_hash=false, script_hash=null.
	 */
	detectThirdParty(url) {
		return !this.isSameOrigin(url);
	}

	toHex(buffer) {
		const bytes = new Uint8Array(buffer);
		let hex = '';
		for (const b of bytes) {
			hex += b.toString(16).padStart(2, '0');
		}
		return hex;
	}
}

export default HashCalculator;

